import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInterval } from "react-use";
import { isPresent } from "@apl-digital/utils";

import Warning from "@/base/components/Error/Warning";
import { H4 } from "@/base/components/Global";
import Button from "@/base/components/Global/Button";
import P from "@/base/components/Global/P";
import { Row } from "@/base/components/Layout";
import Col from "@/base/components/Layout/Col";
import {
  clearSessionStorage,
  clearStorage,
  getSessionStorage,
  SessionStorageKey,
  setSessionStorage,
} from "@/base/utils/storage";
import config from "@/constants/config";
import { createUseStyles } from "@/theme";

const useStyles = createUseStyles(({ spacing }) => ({
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  topRow: {
    marginTop: spacing.x4l,
  },
  redirectBtn: {
    marginTop: "auto",
    marginBottom: "30%",
  },
}));

const reloadWebsite = () => {
  clearStorage(undefined, { includeUnmanaged: true });
  clearSessionStorage(
    [
      SessionStorageKey.SENTRY_REPLAY_SESSION,
      SessionStorageKey.RETRY_COUNTER_REFRESH,
    ],
    {
      mode: "keep",
      includeUnmanaged: true,
    },
  );

  window.open("/", "_self");
};

type ErrorPageProps = {
  resetError?: () => void;
};

const ErrorPage: React.FC<ErrorPageProps> = ({ resetError }) => {
  const classes = useStyles();
  const { t } = useTranslation("errorPage");
  const [counter, setCounter] = useState(3);
  const randomIndex = useMemo(() => Math.floor(Math.random() * 10), []);

  const retryCounter = getSessionStorage(
    SessionStorageKey.RETRY_COUNTER_REFRESH,
    0,
  );

  const isAtMaxRetryCount =
    (isPresent(config.appConfig.errorPageMaxRetryCount) &&
      retryCounter >= config.appConfig.errorPageMaxRetryCount) ||
    !isPresent(config.appConfig.errorPageMaxRetryCount);

  useEffect(() => {
    // stop auto refresh after three tries
    if (!isAtMaxRetryCount && counter === 0) {
      setSessionStorage(
        SessionStorageKey.RETRY_COUNTER_REFRESH,
        retryCounter + 1,
      );

      reloadWebsite();
    }
  }, [retryCounter, counter]);

  useInterval(
    () => {
      setCounter(counter - 1);
    },
    !isAtMaxRetryCount && counter > 0 ? 1000 : null,
  );

  const quotes = t("quotes", { returnObjects: true });

  return (
    <div className={classes.container}>
      <Row className={classes.topRow}>
        <Col col={12}>
          <H4>{quotes[randomIndex].message}</H4>
        </Col>
        <Col col={12} alignEnd>
          <P>{quotes[randomIndex].author}</P>
        </Col>
        <Col col={12}>
          <Warning
            message={t(isAtMaxRetryCount ? "no_retry_msg" : "main_msg")}
          />
        </Col>
      </Row>
      <Row className={classes.redirectBtn}>
        <Col col={12}>
          <Button
            action={() => {
              if (resetError) {
                resetError();
              } else {
                reloadWebsite();
              }
            }}
          >
            {isAtMaxRetryCount
              ? t("redirect_manually_btn")
              : t("redirect_btn", { count: counter })}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default ErrorPage;
